/* eslint-disable react/display-name */
import { openModal } from "action/modal/openModal";
import { saveItem } from "action/shopping-cart/saveItem";
import { setOverviewId } from "action/utils/setOverviewId";
import { setProductDeliveryDetails } from "action/utils/setProductDeliveryDetails";
import { setSyncedProduct } from "action/utils/setSyncedProduct";
import AmpService from "analytics/amplitude/amplitudeEvents";
import KlaviyoTrackingService from "analytics/klaviyo/klaviyoEvents";
import { IDetailedProductResponse } from "api/interfaces/get-page-details/product-page.type";
import { IPageContent } from "api/interfaces/static-content.type";
import { getCategoryPageRecommendation } from "api/product/getCategoryPageRecommendation";
import { saveRecentlyViewedProductApi } from "api/product/saveRecentlyViewedProductApi";
import PagePlaceholder, { PLACEHOLDER_PAGE_TYPE } from "component/category/PagePlaceholder";
import ProductOverview from "component/product/V2/overview/ProductOverview";
import { MAKE_MODEL_YEAR, YEAR_MAKE_MODEL } from "component/vehicle-form/constants";
import { VEHICLE_FITMENT } from "constant/shared-constants";
import useScreenSize from "hoc/useScreenSize";
import ProductContext from "hooks-contexts/ProductContext";
import { RootState } from "interfaces/generics.type";
import { IVehicle } from "interfaces/session/IVehicle";
import AffirmLoaderScript from "modules/checkout/s3/AffirmLoaderScript";
import PageContainer from "modules/components/PageContainer";
import { productNotFitHelper } from "modules/fitment/fitment-helper";
import ProductDeliveryMethodSelector from "modules/shopping-cart/delivery-method-selector/ProductDeliveryMethodSelector";
import ShippingCheckEstimatorNew from "modules/shopping-cart/shipping-check/ShippingCheckEstimatorNew";
import RatingUI from "modules/third-party/sitejabber/RatingUI";
import { useTranslation } from "next-i18next";
import dynamic from "next/dynamic";
import { useRouter } from "next/router";
import { useContext, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { sendGA4ProductViewEvent } from "util/enhanced-ecommerce";
import { getVehicleName } from "util/helper/getVehicleName";
import { priceDifferenceInPercentage } from "util/helper/priceDifferenceInPercentage"; // 12.51KB
import { CATEGORY_PAGE_TYPES, PAGE_TYPES } from "util/product/product-constants";
import { refreshData } from "util/refreshData";
import { scrollToTop } from "util/scroll-helper";
import { getHrefAndPath } from "util/url/getHrefAndPath";
import { validateVehicle } from "util/vehicle/validate-vehicle";
import { isApparelProduct } from "util/product/apparel";
import ProductCommon from "./ProductCommon";
import ProductPageHeader from "./header/ProductPageHeader";
import HelpDesk from "./help-desk/HelpDesk";
import ProductMetaHead from "./meta/ProductMetaHead";
import ProductDelivery from "./product-delivery/ProductDelivery";

import Breadcrumbs from "./header/Breadcrumbs";
import styles from "./product.module.scss";

// import ProductRegular from "./ProductRegular";
// import ProductCompact from "./ProductCompact";

const ProductRegular = dynamic(
  () => import(/* webpackChunkName: "product-desktop" */ "./ProductRegular"),
  { loading: () => <PagePlaceholder pageType={PLACEHOLDER_PAGE_TYPE.PRODUCT_PAGE} /> },
); // 19.58KB
const ProductCompact = dynamic(
  () => import(/* webpackChunkName: "product-mobile" */ "./product-compact/ProductCompact"),
  { loading: () => <PagePlaceholder pageType={PLACEHOLDER_PAGE_TYPE.PRODUCT_PAGE} /> },
);

interface IProps {
  actualResponse: IDetailedProductResponse;
  staticContent: IPageContent;
  pageType: string;
  isSSR: boolean;
  clientSideProductApiResReady?: boolean;
}

function ProductContainer(props: IProps) {
  const {
    actualResponse, staticContent, pageType, RAN_FIRST_TIME,
    clientSideProductApiResReady, locale,
  } = props;
  const {
    product, productImages, productImagesV2, details, productQuestions, productReviews, breadCrumbsV2,
    manufacturerPageUrl, limitedQuantityDetails, installationTips, fitmentOptionsPrompt, fitmentOptionsDetails,
  } = actualResponse;

  const { breadcrumbs } = staticContent;

  const { t } = useTranslation("common");

  const { postalCode, hasRefreshed } = useSelector((state: RootState) => state.shoppingCart);
  const { selectedVehicle, isLoading, isCompleteVehicleSelected } = useSelector((state: RootState) => state.garage);
  const { urlInformation, useVehicleSelectionOptionsModal, previousPageType } = useSelector((store: RootState) => store.utils);
  const { yearRange } = urlInformation;
  const { selectedId } = useSelector((state: RootState) => state.modal);
  const { businessCustomer } = useSelector((store: RootState) => store.authenticationState);
  const dispatch = useDispatch();
  const router = useRouter();
  const { pathname } = router.query;
  const [isMobile, , isDesktop] = useScreenSize();
  const { updateCategoryPageRecommendationData } = useContext(ProductContext);
  const isApparel = isApparelProduct(product);

  const fitmentRef = useRef(null);

  const {
    price = 0, wasPrice = 0, outOfStock, sku, openGuaranteedFitProductOverlay,
    partNumber, primaryCategory, category,
  } = product || {};
  const isProductNotFit = productNotFitHelper(product);
  // new guaranteed fit overlay will be shown only when product does not fit when vehicle fitment options are partially selected
  // this overlay will not be shown on categories where old vehicle selection is used bcz they don't have any logic for partial fitment options
  const showNewGuaranteedFitDesign = validateVehicle(selectedVehicle) && !openGuaranteedFitProductOverlay && useVehicleSelectionOptionsModal && !isCompleteVehicleSelected && isProductNotFit;

  let productOverviewSection: any = null;
  const productOverviewSectionRef = (element: any) => {
    productOverviewSection = element;
  };

  function clientSideRefresh(vehicle: IVehicle) {
    return refreshData({
      pathname,
      isMobile,
      selectedVehicle: vehicle,
    });
  }

  useEffect(() => {
    scrollToTop();

    saveRecentlyViewedProduct();
    dispatch(setSyncedProduct({ quantity: 1, sku }));

    // if (pageType === PAGE_TYPES.PRODUCT) {
    //   AmpService.ProductPageViewed({
    //     sku,
    //     uiPrice: price,
    //     percentDiscount: wasPrice > price ? `${priceDifferenceInPercentage(wasPrice, price)}` : "NOT_PRESENT",
    //   }, product);
    // }

    return (() => {
      dispatch(setProductDeliveryDetails(undefined));
      dispatch(setSyncedProduct(null));
    });
  }, []);

  // useEffect(() => {
  //   yieldToMain().then(() => {
  //     if (window.affirm && window.affirm.ui && typeof window.affirm.ui.refresh === "function") {
  //       window.affirm?.ui?.refresh();
  //     }
  //   });
  // }, []);

  useEffect(() => {
    if (pageType === PAGE_TYPES.PRODUCT) {
      dispatch(setSyncedProduct({ quantity: 1, sku }));
      AmpService.ProductPageViewed({
        sku,
        uiPrice: price,
        percentDiscount: wasPrice > price ? `${priceDifferenceInPercentage(wasPrice, price)}` : "NOT_PRESENT",
        postalCode: postalCode || "NOT_PRESENT",
      }, product);
      KlaviyoTrackingService.productViewed(product);
    }
    sendGA4ProductViewEvent(product, pageType);
  }, [sku]);

  useEffect(() => {
    setTimeout(() => {
      vehicleSelector();
    }, 0);
  }, [router.asPath]);

  useEffect(() => {
    // Log Vehicle Selectment/Fitment Status Change on Product Page
    const fitmentStatusAvailable = ["GUARANTEED_FIT", "NO_FIT"].includes(product.fitmentStatus);
    const fitmentOptions = selectedVehicle?.fitmentOptions?.map((item) => `${item.optionName}=${item.optionValue}`).join(" ");
    const fitmentTitle = `${getVehicleName(selectedVehicle)}${fitmentOptions}`;

    if (RAN_FIRST_TIME && !isLoading) {
      if (fitmentRef.current && fitmentRef.current !== fitmentTitle && fitmentStatusAvailable) {
        if (!(product.fitment === VEHICLE_FITMENT.UNIVERSAL)) {
          if (product.fitmentStatus === "GUARANTEED_FIT") {
            AmpService.ProductVehicleFitment({
              product,
              vehicle: selectedVehicle,
              fitment: true,
            });
          } else if (product.fitmentStatus === "NO_FIT") {
            AmpService.ProductVehicleFitment({
              product,
              vehicle: selectedVehicle,
              fitment: false,
            });
          }
        }
      }

      fitmentRef.current = selectedVehicle ? fitmentTitle : "noVehicleSelected";
    }
  }, [RAN_FIRST_TIME, product]);

  useEffect(() => {
    // routeChangeComplete will not work on page reload so to call engine modal on product page
    if (isLoading === false && hasRefreshed && RAN_FIRST_TIME) {
      vehicleSelector();
    }
  }, [isLoading, hasRefreshed, RAN_FIRST_TIME]); // selected vehicle dependency to open tire size 2nd modal when selecting FO on first modal on product page.
  // RAN_FIRST_TIME is added in deps to delay opening vehicle selector until we get fitmentOptionsPrompt

  useEffect(() => {
    // routeChangeComplete will not work on page reload so to call engine modal on product page
    if (isLoading === false && hasRefreshed && RAN_FIRST_TIME && useVehicleSelectionOptionsModal) {
      vehicleSelector();
    }
  }, [isLoading, hasRefreshed, RAN_FIRST_TIME, selectedVehicle]);

  useEffect(() => {
    if (!openGuaranteedFitProductOverlay) {
      return;
    }
    getCategoryPageRecommendation(product.sku, selectedVehicle)
      .then(({ data }) => {
        const { href, asPath } = getHrefAndPath({ query: data?.queryParams, asPath: data?.url });
        updateCategoryPageRecommendationData({ ...data, href, asPath });
      });
    AmpService.guaranteedFitProduct({ sku: product.sku, vehicle: selectedVehicle }, "PRODUCT_DOES_NOT_FIT");
  }, [openGuaranteedFitProductOverlay, selectedVehicle?.make, selectedVehicle?.model, selectedVehicle?.year, product.sku]);

  function vehicleSelector() {
    // When changing URL parameters
    // skip vehicle selector modal for B2B customer
    const skipVehicleSelectorModal = ["checkFitmentModel", "shoppingCart"].includes(selectedId) || businessCustomer;
    if (skipVehicleSelectorModal) {
      return;
    }

    if (pageType === PAGE_TYPES.PRODUCT_MAKE_MODEL_PAGE && !selectedVehicle) {
      const dMake = urlInformation.make;
      const dModel = urlInformation.model;
      dispatch(openModal("vehicleSelector", {
        title: `${t("Select Year for")} ${dMake} ${dModel}`,
        years: yearRange,
        defaultMake: urlInformation.make,
        defaultModel: urlInformation.model,
        selectionOrder: MAKE_MODEL_YEAR,
        clientSideRefresh,
        fitmentOptionsPrompt,
        fitmentOptionsDetails,
      }));
    } else if (selectedVehicle) {
      dispatch(openModal("vehicleSelector", {
        title: t("Complete your vehicle details"),
        // years,
        defaultMake: selectedVehicle.make,
        defaultModel: selectedVehicle.model,
        defaultYear: selectedVehicle.year,
        selectionOrder: YEAR_MAKE_MODEL,
        clientSideRefresh,
        fitmentOptionsPrompt,
        fitmentOptionsDetails,
      }));
    }
  }

  function handleProductOverviewSectionExpand(section: any) {
    dispatch(setOverviewId(section));
    if (productOverviewSection) {
      productOverviewSection.expandSection(section);
    }
  }

  function saveRecentlyViewedProduct() {
    saveRecentlyViewedProductApi(sku);
  }

  function handleSaveItemForLater(__: any, saveOrRemove = true, callback = null) {
    dispatch(saveItem(
      product,
      selectedVehicle,
      callback,
      saveOrRemove ? product.fitment === VEHICLE_FITMENT.UNIVERSAL : true,
      saveOrRemove ? 1 : 0,
    ));
  }

  const propsToPass = {
    pageType,
    product,
    staticContent,
    productReviews,
    productQuestions,
    details,
    productOverviewSectionRef,
    handleSaveItemForLater,
    productImages,
    productImagesV2,
    handleProductOverviewSectionExpand,
    breadCrumbsV2,
    outOfStock,
    limitedQuantityDetails,
    components: {
      ProductDelivery,
      RatingUI,
      ProductOverview,
      ShippingCheckEstimatorNew,
      ProductDeliveryMethodSelector,
      HelpDesk,
      ProductCommon,
      PageContainer,
      ProductPageHeader,
    },
    wasPrice,
    price,
    defaultVehicle: {
      defaultMake: urlInformation.make,
      defaultModel: urlInformation.model,
      defaultYear: urlInformation.year,
    },
    manufacturerPageUrl,
    installationTips,
    fitmentOptionsDetails,
    years: yearRange,
    showNewGuaranteedFitDesign,
    clientSideProductApiResReady,
  };

  return (
    <>
      <Breadcrumbs
        {...{
          category,
          primaryCategory,
          breadCrumbsV2,
          breadcrumbs,
          staticContent,
          partNumber,
          openGuaranteedFitProductOverlay,
        }}
        showBackToResult={CATEGORY_PAGE_TYPES.includes(previousPageType)}
        className={`${styles.compact_breadcrumb} ${isApparel ? styles.apparel_breadcrumb : styles.product_breadcrumb}`}
      />

      <div
        style={{ fontSize: "calc(1.5 * var(--safari-root-font, 10px))" }}
        className={`test-product-container ${styles.product_container}`}
        data-clarity-unmask="true"
      >

        <ProductMetaHead {...{
          ...product,
          product,
          pageType,
          productQuestions,
          productReviews,
          staticContent,
          breadCrumbsList: breadCrumbsV2[0] || [],
          years: yearRange,
          productImagesV2,
          productImages,
          locale,
        }}
        />
        <AffirmLoaderScript />
        {isDesktop ? <ProductRegular {...propsToPass} /> : <ProductCompact {...propsToPass} showOnlyButton />}

      </div>
    </>
  );
}

export default ProductContainer;
